/**
 * useCredentialStatusProbe — hook that encapsulates the probe state logic so
 * CredentialsTable can render both the badge (in the Status column) and the
 * action button (in the Actions column) as siblings sharing the same state.
 *
 * Returns the displayState, displayLastTestedAt, isProbing, and handleTest.
 * Consumed by `CredentialStatusRow` in `CredentialsTable.tsx`.
 * (`CredentialStatusCard.tsx` keeps its probe state inline for code locality —
 * the card is a self-contained edit-page component whose probe logic is
 * interleaved with its rendering. The hook exists to share probe state across
 * the two sibling cells in the table row.)
 *
 * Kept in its own file to satisfy react-refresh/only-export-components
 * (fast-refresh requires component-only files for HMR support).
 *
 * Dashboard-era only. Brand guide §38. Slice 3.1 — credential-status Epic 3.
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '@/redux'
import type { CredentialConnectionStatus } from '@/types/credentials'
import { CredentialCapabilityAPI } from '@/API/CredentialCapability'
import {
  DisplayStatusState,
  deriveStatusFromPerDomainMap,
} from './credentialStatus'

export function useCredentialStatusProbe(
  credentialId: string,
  connectionStatus: CredentialConnectionStatus | undefined,
) {
  const token = useSelector((s: RootState) => s.user.token)
  const [isProbing, setIsProbing] = useState(false)
  const [localState, setLocalState] = useState<DisplayStatusState | null>(null)
  const [localLastTestedAt, setLocalLastTestedAt] = useState<string | null>(null)

  // Guards the post-await setState calls so a probe that resolves after the row
  // unmounts (user navigated away mid-flight) doesn't warn / leak.
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const displayState: DisplayStatusState = isProbing
    ? 'testing'
    : localState ?? connectionStatus?.state ?? 'not_tested_yet'
  const displayLastTestedAt: string | null =
    localLastTestedAt ?? connectionStatus?.lastTestedAt ?? null

  const handleTest = useCallback(async () => {
    if (!token || isProbing) return
    setIsProbing(true)
    setLocalState(null)

    const resp = await CredentialCapabilityAPI.probeAllDomains(token, credentialId)

    if (!isMountedRef.current) return

    if (resp.err !== undefined || resp.data === undefined) {
      setLocalState('couldnt_verify')
    } else {
      // resp.data is CredentialProbeRawMap (Record<domainId, CredentialCapabilityAvailability>).
      setLocalState(deriveStatusFromPerDomainMap(resp.data))
    }

    setLocalLastTestedAt(new Date().toISOString())
    setIsProbing(false)
  }, [token, credentialId, isProbing])

  return { displayState, displayLastTestedAt, isProbing, handleTest }
}
